import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import {PRICE_ITEM_RELS_DATA} from "@/modules/ClientPriceElementForm/_store";

const PRODUCT_CONTAINER_DECIMAL_PRECISION = 3;

const normalizeProductContainerDecimal = (value: any): string | null => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
        return null;
    }

    return numericValue.toFixed(PRODUCT_CONTAINER_DECIMAL_PRECISION);
};

interface CounterpartyFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    container_rels: any[];
    targetForDelete: any[];
    form: any;
    stickers: any[];
    schema: any;

    meta: any;
}

const state: CounterpartyFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    container_rels: [],
    targetForDelete: [],
    form: null,
    stickers: [],
    schema: null,

    meta: {},
};

const initState: CounterpartyFormState = cloneDeep(state);

export const PRODUCT_CONTAINER_DATA = 'GET_PRODUCT_CONTAINER_DATA';
export const PRODUCT_CONTAINER_STICKER_DATA = 'GET_PRODUCT_CONTAINER_STICKER_DATA';
export const PRODUCT_CONTAINER_SCHEMA = 'GET_PRODUCT_CONTAINER_SCHEMA';

export const actions: ActionTree<CounterpartyFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRODUCT_CONTAINER_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
                vm.$q(PRODUCT_CONTAINER_STICKER_DATA)
                    .raw('documents.list')
                    .action({
                    columns: {
                        id: 'pcs.id',
                        sticker_id: 'pcs.sticker_id',
                        name: 'vs.name'
                    },
                    from: `s:product_containers_stickers`,
                    from_as: 'pcs',
                    joins: [
                        {
                            join: 's:voc_stickers',
                            as: 'vs',
                            on: {
                                $eq: ['vs.id', 'pcs.sticker_id']
                            }
                        },
                    ],
                    where: {
                        $or: [
                            { $eq: [`pcs.product_container_id`, { $: params.id }] },
                        ]
                    }
                    }),
            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', {
                    ...result.getOperation(PRODUCT_CONTAINER_DATA).getContent(),
                    stickers: result.getOperation(PRODUCT_CONTAINER_STICKER_DATA).getContent().documents
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRODUCT_CONTAINER_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:product_containers`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(PRODUCT_CONTAINER_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve(undefined);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);
        const originalWeight = state.form && state.form.weight;
        const originalVolume = state.form && state.form.volume;
        const normalizedWeight = normalizeProductContainerDecimal(originalWeight);
        const normalizedVolume = normalizeProductContainerDecimal(originalVolume);

        console.debug('[ProductContainerForm/saveForm] scoped weight/volume precision flow started', {
            scopedPrecisionChange: true,
            schemaCode: state.schema && state.schema.code,
            recordId: documentId || null,
            originalWeight,
            originalVolume,
            defaultSerializedWeight: normalizedForm && normalizedForm.weight,
            defaultSerializedVolume: normalizedForm && normalizedForm.volume,
            targetPrecision: PRODUCT_CONTAINER_DECIMAL_PRECISION
        });

        if (normalizedWeight !== null) {
            normalizedForm.weight = normalizedWeight;
        }

        if (normalizedVolume !== null) {
            normalizedForm.volume = normalizedVolume;
        }

        console.debug('[ProductContainerForm/saveForm] weight/volume precision normalized', {
            scopedPrecisionChange: true,
            recordId: documentId || null,
            finalSerializedWeight: normalizedForm && normalizedForm.weight,
            finalSerializedVolume: normalizedForm && normalizedForm.volume,
            targetPrecision: PRODUCT_CONTAINER_DECIMAL_PRECISION
        });

        const stickers = state.stickers.map((item: any) => {
            return {
                id: item.id,
                sticker_id: item.sticker_id
            }
        });

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('PRODUCT_CONTAINER_DATA_SAVE')
                    .update(documentId, normalizedForm)
                    .createManyRelation('product_containers_stickers', 'product_container_id', (createRelated: any) => {
                        console.log(stickers)
                      return stickers.filter((item: any) => !item.id || (typeof (item.id) !== 'string')).map((item, index) => createRelated(item));
                    })
                    .deleteManyRelation('product_containers_stickers', 'product_container_id', (deleteRelated: any) => {
                      return state.targetForDelete.map((item: any, index) => deleteRelated({id: item}));
                    })
            } else {
                saveQuery = vm.$q('PRODUCT_CONTAINER_DATA_SAVE')
                    .create('s:product_containers', normalizedForm)
                    .withManyRelation('product_containers_stickers', 'product_container_id', (createRelated: any) => {
                      return stickers.map((item: any) => createRelated(item));
                    })
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('PRODUCT_CONTAINER_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.debug('[ProductContainerForm/saveForm] save failed', {
                        scopedPrecisionChange: true,
                        recordId: documentId || null,
                        serializedWeight: normalizedForm && normalizedForm.weight,
                        serializedVolume: normalizedForm && normalizedForm.volume,
                        operationError: operationResult.getContent()
                    });
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    console.debug('[ProductContainerForm/saveForm] save succeeded', {
                        scopedPrecisionChange: true,
                        recordId: documentId || null,
                        serializedWeight: normalizedForm && normalizedForm.weight,
                        serializedVolume: normalizedForm && normalizedForm.volume
                    });
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.debug('[ProductContainerForm/saveForm] save request rejected', {
                    scopedPrecisionChange: true,
                    recordId: documentId || null,
                    serializedWeight: normalizedForm && normalizedForm.weight,
                    serializedVolume: normalizedForm && normalizedForm.volume,
                    error
                });
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    addSticker({ commit }, sticker) {
      commit('ADD_STICKER', sticker);
    },

    removeSticker({ commit }, index: number) {
      commit('REMOVE_STICKER', index);
    },

};

export const mutations: MutationTree<CounterpartyFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_FORM_DATA(state, {document, schema, stickers}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
        state.stickers = stickers;
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;
        
        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: CounterpartyFormState, {field, newValue}) {
        switch (field) {
            default:
                state.form[field] = newValue;
        }
    },

    ADD_STICKER(state, sticker) {
      state.stickers.push({
        sticker_id: sticker.id,
        name: sticker.label
      })
    },

    REMOVE_STICKER(state, index) {
      if (state.stickers[index].id) {
        state.targetForDelete.push(state.stickers[index].id)
      }

      state.stickers.splice(index, 1);
    }
};

const getters = {
    fields: (state: CounterpartyFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_product_directory_product_container_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
